/**
 * Tracking Metrics Display Component - Premium Design
 * Shows GPS quality metrics with animations and glassmorphism
 */

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrackingMetrics as MetricsType } from '@/services/tracking-service';
import { Signal, Wifi, Battery, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface TrackingMetricsProps {
  metrics: MetricsType;
}

const AnimatedNumber = ({ value, decimals = 1 }: { value: number; decimals?: number }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 800;
    const steps = 30;
    const increment = (value - displayValue) / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(prev => prev + increment);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return <>{displayValue.toFixed(decimals)}</>;
};

export function TrackingMetrics({ metrics }: TrackingMetricsProps) {
  const getQualityColor = (value: number, thresholds: { good: number; ok: number }) => {
    if (value <= thresholds.good) return 'bg-success/20 text-success border-success/30';
    if (value <= thresholds.ok) return 'bg-warning/20 text-warning border-warning/30';
    return 'bg-destructive/20 text-destructive border-destructive/30';
  };

  const getQualityLabel = (value: number, thresholds: { good: number; ok: number }) => {
    if (value <= thresholds.good) return 'Excelente';
    if (value <= thresholds.ok) return 'Bom';
    return 'Fraco';
  };

  const metricsData = [
    {
      icon: Signal,
      label: 'Precisão (P95)',
      value: metrics.p95Error,
      unit: 'm',
      thresholds: { good: 10, ok: 20 },
      color: 'text-ybr-blue'
    },
    {
      icon: Wifi,
      label: 'Gaps de Dados',
      value: metrics.gapPercentage,
      unit: '%',
      thresholds: { good: 3, ok: 5 },
      color: 'text-ybr-green'
    },
    {
      icon: Battery,
      label: 'Consumo',
      value: metrics.batteryDrain,
      unit: '%/h',
      thresholds: { good: 5, ok: 10 },
      color: 'text-ybr-orange'
    },
    {
      icon: Clock,
      label: 'Reconexão',
      value: metrics.reconnectionTime,
      unit: 's',
      thresholds: { good: 3, ok: 10 },
      color: 'text-purple-400'
    }
  ];

  return (
    <Card className="p-5 bg-gradient-to-br from-card/95 via-card/90 to-card/95 backdrop-blur-xl border-border/50 shadow-lg">
      <motion.h3 
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="font-bold text-base mb-5 text-foreground flex items-center gap-2"
      >
        <div className="w-1 h-5 bg-primary rounded-full" />
        Qualidade do GPS
      </motion.h3>
      
      <div className="grid grid-cols-2 gap-4">
        {metricsData.map((metric, index) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.4 }}
            className="space-y-3 p-4 rounded-2xl bg-gradient-to-br from-background/40 to-background/20 backdrop-blur-sm border border-border/30 hover:border-border/50 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-xl bg-gradient-to-br from-background/60 to-background/40 backdrop-blur-sm ${metric.color}`}>
                <metric.icon className="w-4 h-4" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">{metric.label}</span>
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline gap-1">
                <motion.span 
                  className="text-3xl font-bold text-foreground tabular-nums"
                  key={metric.value}
                  initial={{ scale: 1.2, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <AnimatedNumber value={metric.value} />
                </motion.span>
                <span className="text-sm text-muted-foreground font-medium">{metric.unit}</span>
              </div>

              <Badge
                variant="secondary"
                className={`${getQualityColor(metric.value, metric.thresholds)} text-xs font-medium px-3 py-1 border`}
              >
                {getQualityLabel(metric.value, metric.thresholds)}
              </Badge>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-5 pt-4 border-t border-border/50"
      >
        <div className="flex items-start gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Métricas em tempo real • Meta: precisão ≤10m, gaps ≤3%, consumo ≤5%/h, reconexão &lt;3s
          </p>
        </div>
      </motion.div>
    </Card>
  );
}
